import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';

const CATEGORY_META = {
  project_management: {
    icon: '📊',
    label: 'Gestao de Projetos',
    tone: 'bg-sky-100 text-sky-800 border-sky-200',
    useCase: 'Bom para fluxos com fases claras, acompanhamento diario e controle de prioridade.',
  },
  marketing: {
    icon: '📈',
    label: 'Marketing',
    tone: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    useCase: 'Indicado para campanhas, entregas por canal, budget e calendario de lancamento.',
  },
  development: {
    icon: '💻',
    label: 'Desenvolvimento',
    tone: 'bg-violet-100 text-violet-800 border-violet-200',
    useCase: 'Adequado para backlog, sprint, revisao tecnica e carga por responsavel.',
  },
  software: {
    icon: '💻',
    label: 'Software',
    tone: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    useCase: 'Estrutura orientada a entrega tecnica, priorizacao e acompanhamento de execucao.',
  },
  hr: {
    icon: '👥',
    label: 'Pessoas',
    tone: 'bg-amber-100 text-amber-800 border-amber-200',
    useCase: 'Funciona bem para processos internos com responsaveis, datas e checkpoints.',
  },
  sales: {
    icon: '💰',
    label: 'Vendas',
    tone: 'bg-rose-100 text-rose-800 border-rose-200',
    useCase: 'Melhor para pipeline comercial com etapas, dono da conta e previsao de fechamento.',
  },
  general: {
    icon: '🧩',
    label: 'Operacoes',
    tone: 'bg-slate-100 text-slate-800 border-slate-200',
    useCase: 'Estrutura flexivel para operacoes gerais, eventos e rotinas multipasso.',
  },
};

const COLUMN_TYPE_LABELS = {
  text: 'Texto',
  status: 'Status',
  person: 'Responsavel',
  date: 'Data',
  numbers: 'Numero',
  dropdown: 'Selecao',
  progress: 'Progresso',
  checkbox: 'Checklist',
  tags: 'Tags',
  timeline: 'Timeline',
};

const average = (values) => {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const formatColumnType = (type) => COLUMN_TYPE_LABELS[type] || type || 'Campo';

const getCategoryMeta = (category) => CATEGORY_META[category] || {
  icon: '📋',
  label: category ? category.replace(/_/g, ' ') : 'Template',
  tone: 'bg-slate-100 text-slate-800 border-slate-200',
  useCase: 'Estrutura generica para organizacao inicial do board.',
};

const normalizeTemplate = (template) => {
  const structure = template?.board_structure || {};
  const rawColumns = Array.isArray(template?.columns) && template.columns.length
    ? template.columns
    : (structure.columns || []);
  const rawGroups = Array.isArray(template?.default_groups) && template.default_groups.length
    ? template.default_groups
    : (structure.groups || []);
  const sampleItems = Array.isArray(structure.sample_items)
    ? structure.sample_items
    : Array.isArray(template?.sample_items)
      ? template.sample_items
      : [];

  const columns = rawColumns.map((column, index) => ({
    id: column.id || `${template.id}-column-${index}`,
    title: column.title || column.name || `Coluna ${index + 1}`,
    column_type: column.column_type || column.type || 'text',
    settings: column.settings || {},
  }));

  const groups = rawGroups.map((group, index) => {
    if (typeof group === 'string') {
      return {
        id: `${template.id}-group-${index}`,
        title: group,
        color: null,
      };
    }

    return {
      id: group.id || `${template.id}-group-${index}`,
      title: group.title || group.name || `Grupo ${index + 1}`,
      color: group.color || null,
    };
  });

  const optionCount = columns.reduce((total, column) => {
    const options = Array.isArray(column.settings?.options) ? column.settings.options.length : 0;
    return total + options;
  }, 0);

  const typedColumns = columns.reduce((accumulator, column) => {
    const key = column.column_type || 'text';
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});

  return {
    ...template,
    groups,
    columns,
    sampleItems,
    optionCount,
    typedColumns,
  };
};

const getTemplateDifferences = (template, templates) => {
  if (!template) return [];

  const others = templates.filter((item) => item.id !== template.id);
  if (!others.length) {
    return ['Template unico no catalogo atual.'];
  }

  const differences = [];
  const groupCounts = others.map((item) => item.groups.length);
  const columnCounts = others.map((item) => item.columns.length);
  const optionCounts = others.map((item) => item.optionCount);
  const allOtherTypes = new Set(others.flatMap((item) => Object.keys(item.typedColumns)));
  const exclusiveTypes = Object.keys(template.typedColumns).filter((type) => !allOtherTypes.has(type));

  if (template.groups.length > Math.max(...groupCounts)) {
    differences.push(`Tem a maior segmentacao do catalogo, com ${template.groups.length} grupos.`);
  } else if (template.groups.length < Math.min(...groupCounts)) {
    differences.push(`E o fluxo mais enxuto do catalogo, com ${template.groups.length} grupos.`);
  } else {
    differences.push(`Trabalha com ${template.groups.length} grupos, contra media de ${average(groupCounts).toFixed(1)} nos demais.`);
  }

  if (template.columns.length > Math.max(...columnCounts)) {
    differences.push(`E o template com maior cobertura estrutural: ${template.columns.length} colunas configuradas.`);
  } else if (template.columns.length < Math.min(...columnCounts)) {
    differences.push(`Mantem a estrutura mais compacta: ${template.columns.length} colunas configuradas.`);
  } else {
    differences.push(`Entrega ${template.columns.length} colunas, perto da media de ${average(columnCounts).toFixed(1)} dos demais.`);
  }

  if (exclusiveTypes.length) {
    differences.push(`Tem tipos de campo exclusivos nesta lista: ${exclusiveTypes.map(formatColumnType).join(', ')}.`);
  }

  if (template.optionCount > Math.max(...optionCounts)) {
    differences.push(`Tem o maior nivel de detalhamento configuravel, com ${template.optionCount} opcoes de status/selecao.`);
  } else if (template.optionCount) {
    differences.push(`Soma ${template.optionCount} opcoes configuradas entre status e seletores.`);
  }

  if (template.sampleItems.length) {
    differences.push(`Ja traz ${template.sampleItems.length} exemplos de itens para acelerar a primeira configuracao.`);
  } else {
    differences.push('Nao traz itens de exemplo; ele cria apenas a estrutura do quadro.');
  }

  return differences.slice(0, 4);
};

const getTemplateHeadline = (template, templates) => {
  const differences = getTemplateDifferences(template, templates);
  return differences[0] || 'Escopo equilibrado para iniciar rapidamente.';
};

export default function BoardTemplates({ open, onOpenChange, onBoardCreated, api, workspaces = [], defaultWorkspaceId = '' }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [boardName, setBoardName] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(defaultWorkspaceId);

  useEffect(() => {
    setSelectedWorkspaceId(defaultWorkspaceId);
  }, [defaultWorkspaceId, open]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const normalizedTemplates = useMemo(
    () => templates.map(normalizeTemplate),
    [templates]
  );

  const selectedTemplate = useMemo(
    () => normalizedTemplates.find((template) => template.id === selectedTemplateId) || normalizedTemplates[0] || null,
    [normalizedTemplates, selectedTemplateId]
  );

  const templateDifferences = useMemo(
    () => getTemplateDifferences(selectedTemplate, normalizedTemplates),
    [selectedTemplate, normalizedTemplates]
  );

  useEffect(() => {
    if (!normalizedTemplates.length) {
      setSelectedTemplateId('');
      return;
    }

    const stillExists = normalizedTemplates.some((template) => template.id === selectedTemplateId);
    if (!stillExists) {
      setSelectedTemplateId(normalizedTemplates[0].id);
    }
  }, [normalizedTemplates, selectedTemplateId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects/templates');
      const data = response.data;
      setTemplates(Array.isArray(data) ? data : (data?.templates || data?.items || []));
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erro ao carregar templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !boardName.trim()) {
      toast.error('Selecione um template e insira um nome');
      return;
    }
    if (workspaces.length > 0 && !selectedWorkspaceId) {
      toast.error('Selecione um workspace para o quadro');
      return;
    }

    setLoading(true);
    try {
      const params = { board_name: boardName };
      if (selectedWorkspaceId) params.workspace_id = selectedWorkspaceId;

      const response = await api.post(
        `/projects/boards/from-template/${selectedTemplate.id}`,
        null,
        { params }
      );

      onBoardCreated(response.data);
      onOpenChange(false);
      setBoardName('');
    } catch (error) {
      console.error('Error creating board from template:', error);
      toast.error('Erro ao criar quadro do template');
    } finally {
      setLoading(false);
    }
  };

  const categoryCount = new Set(normalizedTemplates.map((template) => template.category)).size;
  const averageGroups = average(normalizedTemplates.map((template) => template.groups.length));
  const averageColumns = average(normalizedTemplates.map((template) => template.columns.length));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-1rem)] max-w-[1380px] flex-col overflow-hidden border-slate-200 p-0 sm:w-[calc(100vw-2rem)]"
        closeClassName="text-white opacity-90 hover:opacity-100 hover:bg-white/10 data-[state=open]:bg-white/10 data-[state=open]:text-white"
      >
        <DialogHeader className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_30%),linear-gradient(135deg,_#0f172a,_#1e293b_58%,_#334155)] px-5 py-5 text-white sm:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <DialogTitle className="text-2xl font-semibold">Templates de quadro</DialogTitle>
              <p className="mt-2 max-w-3xl text-sm text-slate-300">
                Compare escopo, estrutura e diferencas entre os templates antes de criar o board.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="rounded-full border-white/15 bg-white/10 px-3 py-1 text-white">
                {normalizedTemplates.length} templates
              </Badge>
              <Badge variant="outline" className="rounded-full border-white/15 bg-white/10 px-3 py-1 text-white">
                {categoryCount} categorias
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          {loading && templates.length === 0 ? (
            <div className="flex h-72 items-center justify-center rounded-3xl border border-slate-200 bg-white">
              <div className="text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <p className="text-sm text-slate-500">Carregando templates...</p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center">
              <div className="text-4xl">📋</div>
              <h3 className="mt-4 text-xl font-semibold text-slate-900">Nenhum template disponivel</h3>
              <p className="mt-2 text-sm text-slate-500">
                O catalogo nao retornou templates configurados no momento.
              </p>
            </div>
          ) : (
            <div className="space-y-6 pb-6">
              <section className="grid gap-4 md:grid-cols-3">
                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Cobertura media</p>
                    <p className="mt-3 text-3xl font-bold text-slate-900">{averageColumns.toFixed(1)}</p>
                    <p className="mt-2 text-sm text-slate-500">colunas por template no catalogo atual</p>
                  </CardContent>
                </Card>
                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Segmentacao media</p>
                    <p className="mt-3 text-3xl font-bold text-slate-900">{averageGroups.toFixed(1)}</p>
                    <p className="mt-2 text-sm text-slate-500">grupos estruturais por template</p>
                  </CardContent>
                </Card>
                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Leitura comparativa</p>
                    <p className="mt-3 text-base font-semibold text-slate-900">
                      Cada card mostra escopo, estrutura e o principal diferencial do template.
                    </p>
                  </CardContent>
                </Card>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Catalogo comparativo</p>
                    <h3 className="mt-1 text-xl font-semibold text-slate-900">Escolha pelo escopo, nao apenas pelo nome</h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    Selecione um template para ver cobertura completa e diferencas frente aos demais.
                  </p>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                  {normalizedTemplates.map((template) => {
                    const meta = getCategoryMeta(template.category);
                    const isSelected = selectedTemplate?.id === template.id;

                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(template.id)}
                        className={`rounded-3xl border p-4 text-left transition-all ${
                          isSelected
                            ? 'border-blue-200 bg-blue-50 shadow-sm ring-2 ring-blue-500/10'
                            : 'border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-2xl text-white">
                              {template.icon || meta.icon}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-lg font-semibold text-slate-900">{template.name}</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Badge className={`border ${meta.tone}`}>{meta.label}</Badge>
                                <Badge variant="outline" className="border-slate-200 text-slate-600">
                                  {template.groups.length} grupos
                                </Badge>
                                <Badge variant="outline" className="border-slate-200 text-slate-600">
                                  {template.columns.length} colunas
                                </Badge>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <span className="rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white">
                              Selecionado
                            </span>
                          )}
                        </div>

                        <p className="mt-4 text-sm text-slate-600">{template.description}</p>

                        <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                          <div className="rounded-2xl bg-white px-3 py-3">
                            <p className="text-slate-400">Grupos</p>
                            <p className="mt-1 text-base font-semibold text-slate-900">{template.groups.length}</p>
                          </div>
                          <div className="rounded-2xl bg-white px-3 py-3">
                            <p className="text-slate-400">Colunas</p>
                            <p className="mt-1 text-base font-semibold text-slate-900">{template.columns.length}</p>
                          </div>
                          <div className="rounded-2xl bg-white px-3 py-3">
                            <p className="text-slate-400">Opcoes</p>
                            <p className="mt-1 text-base font-semibold text-slate-900">{template.optionCount}</p>
                          </div>
                          <div className="rounded-2xl bg-white px-3 py-3">
                            <p className="text-slate-400">Exemplos</p>
                            <p className="mt-1 text-base font-semibold text-slate-900">{template.sampleItems.length}</p>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          {template.groups.slice(0, 4).map((group) => (
                            <span
                              key={group.id}
                              className="rounded-full px-3 py-1 text-xs font-medium text-slate-700"
                              style={{ backgroundColor: group.color || '#E2E8F0' }}
                            >
                              {group.title}
                            </span>
                          ))}
                          {template.groups.length > 4 && (
                            <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                              +{template.groups.length - 4} grupos
                            </span>
                          )}
                        </div>

                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Diferencial</p>
                          <p className="mt-2 text-sm text-slate-700">{getTemplateHeadline(template, normalizedTemplates)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {selectedTemplate && (
                <section className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
                  <div className="space-y-6">
                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5 sm:p-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Escopo do template</p>
                            <h3 className="mt-1 text-2xl font-semibold text-slate-900">{selectedTemplate.name}</h3>
                            <p className="mt-2 text-sm text-slate-600">{selectedTemplate.description}</p>
                          </div>
                          <Badge className={`w-fit border ${getCategoryMeta(selectedTemplate.category).tone}`}>
                            {getCategoryMeta(selectedTemplate.category).label}
                          </Badge>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-2xl bg-slate-50 px-4 py-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Grupos</p>
                            <p className="mt-2 text-3xl font-bold text-slate-900">{selectedTemplate.groups.length}</p>
                            <p className="mt-1 text-xs text-slate-500">frentes ou fases de trabalho</p>
                          </div>
                          <div className="rounded-2xl bg-slate-50 px-4 py-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Colunas</p>
                            <p className="mt-2 text-3xl font-bold text-slate-900">{selectedTemplate.columns.length}</p>
                            <p className="mt-1 text-xs text-slate-500">campos que ja nascem configurados</p>
                          </div>
                          <div className="rounded-2xl bg-slate-50 px-4 py-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Opcoes</p>
                            <p className="mt-2 text-3xl font-bold text-slate-900">{selectedTemplate.optionCount}</p>
                            <p className="mt-1 text-xs text-slate-500">status e seletores prontos para uso</p>
                          </div>
                          <div className="rounded-2xl bg-slate-50 px-4 py-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Itens modelo</p>
                            <p className="mt-2 text-3xl font-bold text-slate-900">{selectedTemplate.sampleItems.length}</p>
                            <p className="mt-1 text-xs text-slate-500">exemplos iniciais do fluxo</p>
                          </div>
                        </div>

                        <div className="mt-6 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Fases e grupos criados</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {selectedTemplate.groups.map((group) => (
                                <span
                                  key={group.id}
                                  className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-800"
                                  style={{ backgroundColor: group.color || '#E2E8F0' }}
                                >
                                  {group.title}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <p className="text-sm font-semibold text-slate-900">Cobertura do template</p>
                            <p className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                              {getCategoryMeta(selectedTemplate.category).useCase}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5 sm:p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Estrutura detalhada</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Campos que serao criados no board</h3>

                        <div className="mt-5 space-y-3">
                          {selectedTemplate.columns.map((column) => {
                            const options = Array.isArray(column.settings?.options) ? column.settings.options : [];

                            return (
                              <article key={column.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900">{column.title}</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      <Badge variant="outline" className="border-slate-200 text-slate-600">
                                        {formatColumnType(column.column_type)}
                                      </Badge>
                                      {options.length > 0 && (
                                        <Badge variant="outline" className="border-slate-200 text-slate-600">
                                          {options.length} opcoes
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {options.length > 0 && (
                                    <div className="flex max-w-full flex-wrap gap-2 lg:justify-end">
                                      {options.slice(0, 6).map((option) => (
                                        <span key={option} className="rounded-full bg-white px-3 py-1 text-xs text-slate-600">
                                          {option}
                                        </span>
                                      ))}
                                      {options.length > 6 && (
                                        <span className="rounded-full bg-white px-3 py-1 text-xs text-slate-600">
                                          +{options.length - 6}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5 sm:p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Itens de exemplo</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Como o fluxo nasce preenchido</h3>

                        {selectedTemplate.sampleItems.length > 0 ? (
                          <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            {selectedTemplate.sampleItems.map((item, index) => {
                              const group = selectedTemplate.groups.find((entry) => entry.id === item.group || entry.title === item.group);
                              return (
                                <div key={`${item.name}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                                  <p className="font-semibold text-slate-900">{item.name}</p>
                                  <p className="mt-2 text-sm text-slate-500">
                                    Grupo inicial: {group?.title || item.group || 'Nao informado'}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                            Este template nao traz itens modelo. Ele cria apenas a espinha dorsal do board para voce preencher com o seu processo.
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-6">
                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5 sm:p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Diferencas para os demais</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">O que muda neste template</h3>

                        <div className="mt-5 space-y-3">
                          {templateDifferences.map((difference, index) => (
                            <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                              {difference}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5 sm:p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Criacao do quadro</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Instanciar este template</h3>

                        <div className="mt-5 space-y-4">
                          {workspaces.length > 0 && (
                            <div>
                              <label className="mb-2 block text-sm font-medium text-slate-700">
                                Workspace <span className="text-rose-500">*</span>
                              </label>
                              <select
                                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                value={selectedWorkspaceId}
                                onChange={(event) => setSelectedWorkspaceId(event.target.value)}
                              >
                                <option value="">Selecione um workspace</option>
                                {workspaces.map((workspace) => (
                                  <option key={workspace.id} value={workspace.id}>
                                    {workspace.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              Nome do novo quadro
                            </label>
                            <Input
                              placeholder={`Ex: ${selectedTemplate.name} - Operacao`}
                              value={boardName}
                              onChange={(event) => setBoardName(event.target.value)}
                              autoFocus
                              className="rounded-2xl border-slate-200"
                            />
                          </div>

                          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                            <p className="text-sm font-semibold text-slate-900">O que sera criado agora</p>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                              <li>{selectedTemplate.groups.length} grupos estruturais.</li>
                              <li>{selectedTemplate.columns.length} colunas configuradas.</li>
                              <li>{selectedTemplate.optionCount} opcoes prontas em campos de status e selecao.</li>
                              <li>{selectedTemplate.sampleItems.length} itens modelo no setup inicial.</li>
                            </ul>
                          </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleCreateFromTemplate}
                            disabled={loading || !boardName.trim()}
                            className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"
                          >
                            {loading ? 'Criando...' : 'Criar quadro'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
